import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Settings,
  Globe,
  Mail,
  Bell,
  Shield,
  Palette,
  Save,
  RefreshCw,
  Check,
  AlertCircle,
  Info,
  ToggleLeft,
  ToggleRight,
  ExternalLink,
} from 'lucide-react';
import { useAdminStore } from '../../store/admin';

const API_URL = import.meta.env.VITE_API_URL || '/api';

// Build version: 2

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Define setting keys and their metadata
const SETTING_DEFINITIONS = {
  // General Settings
  site_name: { label: 'Site Name', description: 'The name of your platform', category: 'general', type: 'text' },
  site_description: { label: 'Site Description', description: 'Brief description of your platform', category: 'general', type: 'textarea' },
  site_url: { label: 'Site URL', description: 'Your primary domain URL', category: 'general', type: 'text' },
  support_email: { label: 'Support Email', description: 'Email for customer support inquiries', category: 'general', type: 'email' },

  // Email Settings
  email_from_name: { label: 'From Name', description: 'Name shown in outgoing emails', category: 'email', type: 'text' },
  email_from_address: { label: 'From Email', description: 'Email address for outgoing emails', category: 'email', type: 'email' },

  // Feature Toggles
  registration_enabled: { label: 'User Registration', description: 'Allow new users to register', category: 'features', type: 'toggle' },
  email_verification_required: { label: 'Email Verification', description: 'Require email verification for new accounts', category: 'features', type: 'toggle' },
  maintenance_mode: { label: 'Maintenance Mode', description: 'Show maintenance page to non-admin users', category: 'features', type: 'toggle' },

  // Branding
  logo_url: { label: 'Logo URL', description: 'URL to your logo image', category: 'branding', type: 'text' },
  favicon_url: { label: 'Favicon URL', description: 'URL to your favicon', category: 'branding', type: 'text' },
  primary_color: { label: 'Primary Color', description: 'Main brand color (hex)', category: 'branding', type: 'color' },
  secondary_color: { label: 'Secondary Color', description: 'Secondary brand color (hex)', category: 'branding', type: 'color' },

  // SEO Settings
  meta_title: { label: 'Meta Title', description: 'Default page title for SEO', category: 'seo', type: 'text' },
  meta_description: { label: 'Meta Description', description: 'Default meta description for SEO', category: 'seo', type: 'textarea' },
  meta_keywords: { label: 'Meta Keywords', description: 'Comma-separated keywords for SEO', category: 'seo', type: 'text' },
  og_image: { label: 'OG Image URL', description: 'Default image for social media sharing', category: 'seo', type: 'text' },

  // Analytics
  google_analytics_id: { label: 'Google Analytics ID', description: 'GA4 Measurement ID (G-XXXXXXXX)', category: 'analytics', type: 'text' },

  // Legal
  terms_url: { label: 'Terms of Service URL', description: 'Link to your terms of service', category: 'legal', type: 'text' },
  privacy_url: { label: 'Privacy Policy URL', description: 'Link to your privacy policy', category: 'legal', type: 'text' },
} as const;

type SettingKey = keyof typeof SETTING_DEFINITIONS;
type Category = 'general' | 'email' | 'features' | 'branding' | 'seo' | 'analytics' | 'legal';

const CATEGORIES: { id: Category; label: string; icon: React.ReactNode; description: string }[] = [
  { id: 'general', label: 'General', icon: <Globe className="w-5 h-5" />, description: 'Basic site information and configuration' },
  { id: 'email', label: 'Email', icon: <Mail className="w-5 h-5" />, description: 'Email sender configuration' },
  { id: 'features', label: 'Features', icon: <Bell className="w-5 h-5" />, description: 'Enable or disable platform features' },
  { id: 'branding', label: 'Branding', icon: <Palette className="w-5 h-5" />, description: 'Customize your platform appearance' },
  { id: 'seo', label: 'SEO', icon: <ExternalLink className="w-5 h-5" />, description: 'Search engine optimization settings' },
  { id: 'analytics', label: 'Analytics', icon: <Settings className="w-5 h-5" />, description: 'Tracking and analytics configuration' },
  { id: 'legal', label: 'Legal', icon: <Shield className="w-5 h-5" />, description: 'Legal documents and compliance' },
];

// Default values for settings
const DEFAULT_VALUES: Record<SettingKey, string> = {
  site_name: 'SweetPilot',
  site_description: 'AI Employees That Never Sleep',
  site_url: 'https://sweetpilot.com',
  support_email: 'support@sweetpilot.com',
  email_from_name: 'SweetPilot',
  email_from_address: 'noreply@sweetpilot.com',
  registration_enabled: 'true',
  email_verification_required: 'false',
  maintenance_mode: 'false',
  logo_url: '',
  favicon_url: '',
  primary_color: '#8B5CF6',
  secondary_color: '#06B6D4',
  meta_title: 'SweetPilot - AI Employees That Never Sleep',
  meta_description: 'Build a team of AI employees that work 24/7. Chat with specialized AI assistants, automate workflows, and scale your business.',
  meta_keywords: 'AI, employees, automation, chatbot, business, productivity',
  og_image: '',
  google_analytics_id: '',
  terms_url: '/terms',
  privacy_url: '/privacy',
};

export default function SiteSettings() {
  const { adminToken } = useAdminStore();
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeCategory, setActiveCategory] = useState<Category>('general');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [originalSettings, setOriginalSettings] = useState<Record<string, string>>({});

  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/admin/site-settings`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });

      if (response.ok) {
        const data = await response.json() as { success: boolean; data: Record<string, { value: string }> };
        if (data.success && data.data) {
          const settingsMap: Record<string, string> = {};
          // Initialize with defaults
          Object.entries(DEFAULT_VALUES).forEach(([key, value]) => {
            settingsMap[key] = value;
          });
          // Override with saved values
          Object.entries(data.data).forEach(([key, { value }]) => {
            if (value) settingsMap[key] = value;
          });
          setSettings(settingsMap);
          setOriginalSettings(settingsMap);
        }
      }
    } catch (err) {
      console.error('Failed to fetch site settings:', err);
      setError('Failed to load site settings');
      // Initialize with defaults on error
      setSettings({ ...DEFAULT_VALUES });
      setOriginalSettings({ ...DEFAULT_VALUES });
    } finally {
      setIsLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    // Check if there are unsaved changes
    const changed = Object.keys(settings).some(key => settings[key] !== originalSettings[key]);
    setHasChanges(changed);
  }, [settings, originalSettings]);

  const handleInputChange = (key: string, value: string) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleToggle = (key: string) => {
    const currentValue = settings[key] === 'true';
    handleInputChange(key, (!currentValue).toString());
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      // Prepare settings array for batch update
      const settingsArray = Object.entries(settings).map(([key, value]) => ({
        key,
        value,
        description: SETTING_DEFINITIONS[key as SettingKey]?.description || '',
      }));

      const response = await fetch(`${API_URL}/admin/site-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({ settings: settingsArray }),
      });

      const result = await response.json() as { success: boolean; error?: string };

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to save settings');
      }

      setOriginalSettings({ ...settings });
      setHasChanges(false);
      setSuccessMessage('Settings saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setSettings({ ...originalSettings });
  };

  const handleResetToDefaults = () => {
    if (window.confirm('Are you sure you want to reset all settings to defaults? This cannot be undone.')) {
      setSettings({ ...DEFAULT_VALUES });
    }
  };

  const renderSettingInput = (key: SettingKey) => {
    const def = SETTING_DEFINITIONS[key];
    const value = settings[key] || '';

    switch (def.type) {
      case 'toggle':
        return (
          <button
            onClick={() => handleToggle(key)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-colors ${
              value === 'true'
                ? 'bg-green-100 text-green-600'
                : 'bg-slate-100 text-slate-500'
            }`}
          >
            {value === 'true' ? (
              <>
                <ToggleRight className="w-5 h-5" />
                <span className="font-medium">Enabled</span>
              </>
            ) : (
              <>
                <ToggleLeft className="w-5 h-5" />
                <span className="font-medium">Disabled</span>
              </>
            )}
          </button>
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(key, e.target.value)}
            placeholder={def.description}
            rows={3}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900 resize-none"
          />
        );

      case 'color':
        return (
          <div className="flex items-center space-x-3">
            <input
              type="color"
              value={value || '#8B5CF6'}
              onChange={(e) => handleInputChange(key, e.target.value)}
              className="w-12 h-10 rounded-lg cursor-pointer border-0 bg-transparent"
            />
            <input
              type="text"
              value={value}
              onChange={(e) => handleInputChange(key, e.target.value)}
              placeholder="#RRGGBB"
              className="flex-1 h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900 font-mono text-sm"
            />
          </div>
        );

      default:
        return (
          <input
            type={def.type === 'email' ? 'email' : 'text'}
            value={value}
            onChange={(e) => handleInputChange(key, e.target.value)}
            placeholder={def.description}
            className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
          />
        );
    }
  };

  const getSettingsForCategory = (category: Category) => {
    return Object.entries(SETTING_DEFINITIONS)
      .filter(([, def]) => def.category === category)
      .map(([key]) => key as SettingKey);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="space-y-6"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1 sm:mb-2">Site Settings</h1>
          <p className="text-sm sm:text-base text-slate-500">
            Configure general platform settings and preferences
          </p>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-3">
          {hasChanges && (
            <button
              onClick={handleReset}
              className="px-3 sm:px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors flex items-center space-x-1 sm:space-x-2 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">Discard Changes</span>
              <span className="sm:hidden">Discard</span>
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
            className="px-4 sm:px-6 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow disabled:opacity-50 flex items-center space-x-1 sm:space-x-2 text-sm"
          >
            {isSaving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span className="hidden sm:inline">Save Changes</span>
                <span className="sm:hidden">Save</span>
              </>
            )}
          </button>
        </div>
      </motion.div>

      {/* Messages */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center space-x-2 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </motion.div>
        )}
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center space-x-2 bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-xl"
          >
            <Check className="w-5 h-5 flex-shrink-0" />
            <span>{successMessage}</span>
          </motion.div>
        )}
        {hasChanges && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center space-x-2 bg-orange-50 border border-orange-200 text-orange-600 px-4 py-3 rounded-xl"
          >
            <Info className="w-5 h-5 flex-shrink-0" />
            <span>You have unsaved changes. Don't forget to save!</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 sm:gap-6">
        {/* Category Sidebar - horizontal scroll on mobile */}
        <motion.div variants={fadeInUp} className="lg:col-span-1">
          <div className="bg-white rounded-xl p-2 border border-slate-200 shadow-sm lg:sticky lg:top-6">
            {/* Mobile: Horizontal scroll */}
            <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0 -mx-2 px-2 lg:mx-0 lg:px-0">
              {CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`flex items-center space-x-2 sm:space-x-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl text-left transition-colors whitespace-nowrap flex-shrink-0 lg:flex-shrink lg:w-full ${
                    activeCategory === category.id
                      ? 'bg-purple-100 text-purple-600'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  {category.icon}
                  <span className="font-medium text-sm sm:text-base">{category.label}</span>
                </button>
              ))}
            </nav>

            {/* Reset to Defaults - hidden on mobile, shown on desktop */}
            <div className="hidden lg:block mt-4 pt-4 border-t border-slate-200">
              <button
                onClick={handleResetToDefaults}
                className="w-full text-sm text-slate-400 hover:text-red-500 transition-colors py-2"
              >
                Reset All to Defaults
              </button>
            </div>
          </div>
        </motion.div>

        {/* Settings Panel */}
        <motion.div variants={fadeInUp} className="lg:col-span-3">
          <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm">
            {/* Category Header */}
            <div className="flex items-center space-x-3 sm:space-x-4 mb-4 sm:mb-6 pb-4 border-b border-slate-200">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-purple-100 flex items-center justify-center text-purple-600 flex-shrink-0">
                {CATEGORIES.find(c => c.id === activeCategory)?.icon}
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900">
                  {CATEGORIES.find(c => c.id === activeCategory)?.label} Settings
                </h2>
                <p className="text-xs sm:text-sm text-slate-500">
                  {CATEGORIES.find(c => c.id === activeCategory)?.description}
                </p>
              </div>
            </div>

            {/* Settings List */}
            <div className="space-y-6">
              {getSettingsForCategory(activeCategory).map((key) => {
                const def = SETTING_DEFINITIONS[key];
                return (
                  <div key={key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-slate-900">
                        {def.label}
                      </label>
                      {def.type === 'toggle' && renderSettingInput(key)}
                    </div>
                    <p className="text-xs text-slate-400">{def.description}</p>
                    {def.type !== 'toggle' && (
                      <div className="mt-2">
                        {renderSettingInput(key)}
                      </div>
                    )}
                  </div>
                );
              })}

              {getSettingsForCategory(activeCategory).length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  No settings available for this category.
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Mobile: Reset to defaults button */}
      <motion.div variants={fadeInUp} className="lg:hidden">
        <button
          onClick={handleResetToDefaults}
          className="w-full text-sm text-slate-400 hover:text-red-500 transition-colors py-2 bg-white rounded-xl border border-slate-200"
        >
          Reset All to Defaults
        </button>
      </motion.div>

      {/* Info Banner */}
      <motion.div variants={fadeInUp} className="bg-slate-50 border border-slate-200 rounded-xl p-3 sm:p-4">
        <div className="flex items-start space-x-2 sm:space-x-3">
          <Info className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs sm:text-sm text-slate-500">
              Changes to site settings may take a few minutes to propagate across all services.
              Some settings like email configuration require valid credentials to work properly.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
